import type { Alignment, EveningSummary, Profession } from '@continuum/show-core';
import { put, wrap } from './chronicleCard.js';
import type { Ctx } from './chronicleCard.js';
import { SCORE_COPY, fmt } from './scoreText.js';
import type { ScoreCopy } from './scoreText.js';
import type { Lang } from './text.js';
import { WRAPPED_COPY } from './wrappedText.js';
import { postName } from './screenText.js';
import { crewTitle, funnyRows, jobMood, paceOf } from './wrappedStats.js';
import type { ChatCounts, FunnyRow, WrappedStats } from './wrappedStats.js';

/**
 * The evening, wrapped: a handful of vertical story cards, 1080×1920, drawn on the guest's own
 * phone and never uploaded anywhere. Like the chronicle card they carry the call sign and
 * nothing else about the guest, no dates, times or brands, and the eras by name rather than
 * by year. Words stay between the top 250 px and the bottom 340 px, clear of the story app's
 * own buttons.
 *
 * The role card is the guest's own role, which their phone already knows. Nobody else's role,
 * and nobody's volunteer answer, is anywhere in here; nor anybody's ballot (wrappedStats.ts).
 *
 * The run, in order, like a story (Marek, 2026-10-10): the evening, what their name says, their job,
 * the fastest game, the best era, them and the crew (their own messages in counts, alarms fixed, votes
 * that hit a saboteur), the other numbers (the funny ones), their role, how it ended, and their crew
 * title last. The same run makes the video (wrappedVideo.ts), five seconds a slide.
 */

export const STORY_W = 1080;
export const STORY_H = 1920;

const INK = '#f3ead6';
const DIM = '#9c93b0';
const MONO = "'IBM Plex Mono', monospace";
const SANS = "'Rajdhani', sans-serif";
const CREW = '#4fd8c2';
const AGENT = '#ff5f86';
const GOLD = '#d9b96a';
const DRIVE = '#a08cff';
/** Each era's own colour, lifted a little where the darkest would not read on black. */
const ERA_ACCENT = { 1: '#6fb7ff', 2: '#ff4fd8', 3: '#ff2e3f', 4: '#e08a3c', 5: '#8fb77a' } as const;
/** Each post's own colour on its slide. */
const POST_ACCENT: Record<Profession, string> = {
  NAVIGATOR: '#6fb7ff',
  ENGINEER: '#ff9f43',
  BIOTECHNICIAN: '#8fd16a',
  'SIGNAL OFFICER': '#c08cff',
  ARCHIVIST: '#e8c27a',
  QUARTERMASTER: '#4fd8c2',
};
const NAME_ACCENT = '#9fd4ff';

export interface WrappedInput {
  lang: Lang;
  summary: EveningSummary;
  /** Set at docking. There is no winner, only the damage (2026-09-29). */
  outcome: 'home' | 'crew' | 'agents' | null;
  damage: number;
  damageLimit: number;
  uprising: boolean;
  /** This phone's own role for the evening — a saboteur at any stop — and only ever this phone's. */
  alignment: Alignment | null;
  /** The eras this guest was a saboteur in (show-core `You.saboteurAt`). */
  saboteurAt?: readonly number[];
  /** Whether someone else shares this guest's final rank. */
  sharedRank: boolean;
  /** …and their rank in their best era. */
  sharedBest: boolean;
  /**
   * The evening's other numbers, real and funny (wrappedStats.ts `wrappedStats`). Without them the run
   * is the scores' cards and a crew title from the scores alone.
   */
  stats?: WrappedStats;
  /**
   * This phone's own messages tonight, counted (wrappedStats.ts `chatCounts`): never their words, never
   * who with. Absent where the phone has no messages from the room.
   */
  chat?: ChatCounts;
}

export interface StoryCard {
  id: string;
  /** Every word the card puts on the picture, header and footer included, as drawn. */
  words: string[];
  /** Draws the card; `page` and `pages` go in the corner. */
  draw: (page: number, pages: number) => Promise<Blob>;
}

/** The biggest size, down from `size`, at which `text` fits in `max` pixels. */
function fitted(ctx: Ctx, text: string, max: number, size: number, weight = 700, family = SANS, spacing = 0): string {
  let px = size;
  ctx.letterSpacing = `${spacing}px`;
  for (; px > 40; px -= 4) {
    ctx.font = `${weight} ${px}px ${family}`;
    if (ctx.measureText(text).width <= max) break;
  }
  return `${weight} ${px}px ${family}`;
}

async function canvasCard(
  accent: string,
  kicker: string,
  callsign: string,
  footer: string,
  page: number,
  pages: number,
  body: (ctx: Ctx) => void,
): Promise<Blob> {
  await document.fonts?.ready;
  const canvas = document.createElement('canvas');
  canvas.width = STORY_W;
  canvas.height = STORY_H;
  const ctx: Ctx | null = canvas.getContext('2d');
  if (!ctx) throw new Error('this phone cannot draw the card');

  const ground = ctx.createLinearGradient(0, 0, 0, STORY_H);
  ground.addColorStop(0, '#100d1a');
  ground.addColorStop(1, '#06060a');
  ctx.fillStyle = ground;
  ctx.fillRect(0, 0, STORY_W, STORY_H);
  // One soft pool of the card's colour behind the big words. Nothing that reads as a flash.
  const glow = ctx.createRadialGradient(STORY_W / 2, 860, 0, STORY_W / 2, 860, 760);
  glow.addColorStop(0, `${accent}2e`);
  glow.addColorStop(1, `${accent}00`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, STORY_W, STORY_H);
  ctx.strokeStyle = `${accent}73`;
  ctx.lineWidth = 3;
  ctx.strokeRect(50, 50, STORY_W - 100, STORY_H - 100);

  put(ctx, kicker, 100, 320, `500 32px ${MONO}`, accent, 6);
  put(ctx, `${page} / ${pages}`, STORY_W - 100, 320, `400 26px ${MONO}`, DIM, 4, 'right');
  put(ctx, `SS AURORA · ${callsign}`, 100, 370, `400 26px ${MONO}`, DIM, 4);
  ctx.fillStyle = `${accent}59`;
  ctx.fillRect(100, 410, STORY_W - 200, 2);

  body(ctx);

  put(ctx, footer, STORY_W / 2, 1560, `400 24px ${MONO}`, DIM, 4, 'center');
  return new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('no image'))), 'image/png'),
  );
}

/** Words onto lines no wider than `max`, in `font`. */
function splitLines(ctx: Ctx, text: string, max: number, font: string, spacing = 0): string[] {
  ctx.font = font;
  ctx.letterSpacing = `${spacing}px`;
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const tried = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(tried).width > max) {
      lines.push(line);
      line = word;
    } else {
      line = tried;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Centre-aligned words onto lines no wider than `max`; returns where the next line would start. */
function wrapCentred(ctx: Ctx, text: string, y: number, max: number, lineHeight: number, font: string, color: string, spacing = 0): number {
  for (const l of splitLines(ctx, text, max, font, spacing)) {
    put(ctx, l, STORY_W / 2, y, font, color, spacing, 'center');
    y += lineHeight;
  }
  return y;
}

/** A row of big numbers with their words under them, down the left: the ship card and the funny card. */
function rows(ctx: Ctx, list: readonly (readonly [string, string])[], top: number, step: number, size: number, accent: string) {
  const wide = STORY_W - 200;
  list.forEach(([big, label], i) => {
    const y = top + i * step;
    put(ctx, big, 100, y, fitted(ctx, big, wide, size, 700, SANS, 4), accent, 4);
    wrap(ctx, label, 100, y + 58, wide, 40, `500 34px ${MONO}`, INK);
  });
}

/** Which cards this guest gets, in order. The chronicle card is added after these by the viewer. */
export function wrappedCards(input: WrappedInput): StoryCard[] {
  const s: ScoreCopy = SCORE_COPY[input.lang];
  const w = WRAPPED_COPY[input.lang];
  const { summary, stats } = input;
  const name = summary.callsign ?? '—';
  const frameWords = (kicker: string) => [kicker, `SS AURORA · ${name}`, s.footer];
  const cards: StoryCard[] = [];
  const add = (id: string, accent: string, kicker: string, words: string[], body: (ctx: Ctx) => void) =>
    cards.push({
      id,
      words: [...frameWords(kicker), ...words],
      draw: (page, pages) => canvasCard(accent, kicker, name, s.footer, page, pages, body),
    });
  const mid = STORY_W / 2;
  const wide = STORY_W - 200;

  // 1 · who you were, and where you finished
  {
    const points = s.points(summary.total);
    // No points, no rank read out: "joint 9th" on a card to share is not a nice thing to keep.
    const line =
      summary.rank === null || summary.total <= 0 ? s.cardYouNone : s.cardYouRank(summary.rank, summary.players, input.sharedRank);
    add('evening', GOLD, s.cardYouKicker, [name, points, line], (ctx) => {
      put(ctx, name, mid, 860, fitted(ctx, name, wide, 230, 700, SANS, 10), INK, 10, 'center');
      put(ctx, points, mid, 1030, `700 104px ${SANS}`, GOLD, 5, 'center');
      wrapCentred(ctx, line, 1120, wide, 50, `500 36px ${MONO}`, INK, 4);
    });
  }

  // 2 · what the name says: every call sign has its own made-up line, tied to the evening by its best era
  {
    const callsign = summary.callsign;
    if (callsign) {
      const meaning = w.nameLine(callsign);
      const tie = w.nameTie(callsign, summary.bestEra ? s.eraName[summary.bestEra.chapter] : null);
      add('name', NAME_ACCENT, w.nameKicker, [callsign, meaning, tie], (ctx) => {
        put(ctx, callsign, mid, 780, fitted(ctx, callsign, wide, 230, 700, SANS, 12), NAME_ACCENT, 12, 'center');
        const y = wrapCentred(ctx, meaning, 920, wide - 40, 56, `500 40px ${MONO}`, INK, 1);
        wrapCentred(ctx, tie, y + 50, wide, 48, `600 34px ${MONO}`, NAME_ACCENT, 3);
      });
    }
  }

  // 3 · their job: the post, a line for how its games went, and its numbers
  const post = stats?.profession ?? null;
  if (post) {
    const accent = POST_ACCENT[post];
    const title = postName(post, input.lang);
    const line = w.jobLine(post, jobMood(summary.games, stats));
    const best = stats?.fastest && stats.fastest.ms !== null ? w.pace(paceOf(stats.fastest)) : '—';
    const tally: [string, string][] = [
      [fmt(summary.games), w.jobGames],
      [best, w.jobBest],
      [stats && stats.counted > 0 ? fmt(stats.totalMistakes) : '—', w.jobMistakes],
    ];
    add('job', accent, w.jobKicker, [title, line, ...tally.flat()], (ctx) => {
      const font = fitted(ctx, title, wide, 190, 700, SANS, 8);
      put(ctx, title, mid, 820, font, accent, 8, 'center');
      wrapCentred(ctx, line, 960, wide - 40, 56, `500 40px ${MONO}`, INK, 1);
      tally.forEach(([n, label], i) => {
        const x = 100 + wide / 6 + (i * wide) / 3;
        put(ctx, n, x, 1340, fitted(ctx, n, wide / 3 - 20, 120, 700, SANS, 4), accent, 4, 'center');
        put(ctx, label, x, 1395, fitted(ctx, label, wide / 3 - 20, 28, 500, MONO, 2), INK, 2, 'center');
      });
    });
  }

  // 4 · the fastest game, against its own par, by its name in the phone's own language
  const fast = stats?.fastest
    ? { chapter: stats.fastest.chapter, label: stats.fastest.title[input.lang], ms: stats.fastest.ms ?? 0, parMs: stats.fastest.parMs }
    : summary.fastest;
  if (fast && fast.ms > 0) {
    const pace = paceOf(fast);
    const time = s.seconds(fast.ms);
    const paceLine = s.cardFastPace(pace);
    const game = fast.label.toUpperCase();
    const par = s.cardFastPar(fast.chapter, fast.parMs);
    add('fastest', DRIVE, s.cardFastKicker, [time, paceLine, game, par], (ctx) => {
      put(ctx, time, mid, 850, `700 230px ${SANS}`, INK, 6, 'center');
      put(ctx, paceLine, mid, 970, fitted(ctx, paceLine, wide, 64, 700, SANS, 4), DRIVE, 4, 'center');
      const y = wrapCentred(ctx, game, 1080, wide, 52, `500 38px ${MONO}`, INK, 2);
      put(ctx, par, mid, y + 20, `400 30px ${MONO}`, DIM, 4, 'center');
    });
  }

  // 5 · the era you scored most in
  const bestEra = summary.bestEra;
  if (bestEra) {
    const accent = ERA_ACCENT[bestEra.chapter];
    const era = s.eraName[bestEra.chapter];
    const line = s.cardBestLine(bestEra.points, bestEra.rank, input.sharedBest);
    add('best-era', accent, s.cardBestKicker, [era, line], (ctx) => {
      const y = wrapCentred(ctx, era, 820, wide, 160, fitted(ctx, era, wide, 170, 700, SANS, 6), INK, 6);
      wrapCentred(ctx, line, y + 10, wide, 50, `500 38px ${MONO}`, accent, 4);
    });
  }

  // 6 · them and the crew: their own messages, counted, the alarms they helped fix and the votes that hit a
  // saboteur — counts only. The votes are the reveals' own, made public at each stop, never anybody's ballot.
  // Without the messages, the games stand where they were (what you did for the ship).
  {
    const { chat } = input;
    const list: [string, string][] = [
      ...(chat
        ? ([
            [fmt(chat.sent), w.chatSent(chat.sent, chat.people)],
            [fmt(chat.received), w.chatGot(chat.received)],
          ] as [string, string][])
        : ([[fmt(summary.games), s.cardGames(summary.games)]] as [string, string][])),
      [fmt(summary.fixes), s.cardFixes(summary.fixes)],
      stats ? [fmt(stats.rightVotes), w.rightVotes(stats.rightVotes)] : [fmt(summary.ballots), s.cardBallots(summary.ballots)],
    ];
    const kicker = chat ? w.crewKicker : s.cardShipKicker;
    add('crew', CREW, kicker, list.flat(), (ctx) =>
      list.length > 3 ? rows(ctx, list, 600, 225, 150, CREW) : rows(ctx, list, 640, 235, 170, CREW),
    );
  }

  // 7 · the other numbers: the funny ones, only those worth a smile, and never at anyone's expense
  const funny = funnyRows(stats);
  if (funny.length > 0) {
    const list = funny.map((row) => funnyLine(row, input.lang));
    add('other', FUNNY_ACCENT, w.otherKicker, list.flat(), (ctx) => rows(ctx, list, 620, 300, 150, FUNNY_ACCENT));
  }

  // 8 · your own role, on your own phone only; a saboteur's own numbers under it
  if (input.alignment) {
    const agent = input.alignment === 'PARADOX AGENT';
    const accent = agent ? AGENT : CREW;
    const role = agent ? s.roleSaboteur : s.roleCrew;
    const stops = [...new Set([...(input.saboteurAt ?? []), ...(stats?.saboteurAt ?? [])])]
      .filter((c): c is 1 | 2 | 3 | 4 | 5 => c >= 1 && c <= 5)
      .sort((a, b) => a - b);
    const eras = stops.map((c) => s.eraName[c]);
    const line = agent ? (eras.length > 0 ? s.roleSaboteurIn(eras) : s.roleSaboteurLine) : s.roleCrewLine;
    const tally: [string, string][] =
      agent && stats
        ? [
            [fmt(stats.escaped), w.gotAway],
            [fmt(stats.caught), w.caught],
            [fmt(stats.damageDone), w.damageDone],
          ]
        : [];
    add('role', accent, s.cardRoleKicker, [role, line, ...tally.flat()], (ctx) => {
      put(ctx, role, mid, 870, fitted(ctx, role, wide, 210, 700, SANS, 10), accent, 10, 'center');
      wrapCentred(ctx, line, 1100, wide - 60, 50, `400 36px ${MONO}`, DIM, 1);
      tally.forEach(([n, label], i) => {
        const x = 100 + wide / 6 + (i * wide) / 3;
        put(ctx, n, x, 1380, `700 120px ${SANS}`, accent, 4, 'center');
        put(ctx, label, x, 1435, fitted(ctx, label, wide / 3 - 20, 28, 500, MONO, 2), INK, 2, 'center');
      });
    });
  }

  // 9 · how the evening ended, for the whole table: no winner (Marek, 2026-09-29), the damage the ship
  // came home with; the scores decide the rest.
  if (input.outcome) {
    const accent = '#d9b96a';
    const home = s.cameHome(input.damage);
    add('result', accent, s.cardResultKicker, input.uprising ? [home, s.cardUprising] : [home], (ctx) => {
      const y = wrapCentred(ctx, home, 780, wide, 130, `700 120px ${SANS}`, accent, 6);
      if (input.uprising) wrapCentred(ctx, s.cardUprising, y + 60, wide, 44, `500 30px ${MONO}`, '#ff9fb3', 3);
    });
  }

  // 10 · the crew title, last: the one to post
  {
    const kind = crewTitle(summary, stats, input.saboteurAt ?? [], input.chat);
    const agentTitle = kind === 'masterSaboteur' || kind === 'slipperySaboteur' || kind === 'boldSaboteur';
    const accent = agentTitle ? AGENT : DRIVE;
    const title = w.title(kind, stats?.profession ?? null);
    const why = w.titleWhy(kind);
    // Nothing about the hidden signal: these cards are made to be shared, and the signal stays a secret for the
    // next evenings' guests (signal.ts).
    add('title', accent, w.titleKicker, [title, why], (ctx) => {
      const longest = title.split(' ').reduce((a, b) => (b.length > a.length ? b : a), '');
      const font = fitted(ctx, longest, wide, 160, 700, SANS, 6);
      // The title's lines centred on the card, however many it takes.
      const lines = splitLines(ctx, title, wide, font, 6).length;
      const y = wrapCentred(ctx, title, 900 - (lines - 1) * 75, wide, 150, font, INK, 6);
      wrapCentred(ctx, why, y + 10, wide, 50, `500 38px ${MONO}`, accent, 2);
    });
  }

  return cards;
}

const FUNNY_ACCENT = '#ffb067';

/** One funny row: the big number or time, and its words, in the phone's language. */
export function funnyLine(row: FunnyRow, lang: Lang): [string, string] {
  const s = SCORE_COPY[lang];
  const w = WRAPPED_COPY[lang];
  switch (row.kind) {
    case 'wronglyOut':
      return [fmt(row.n), w.wronglyOut(row.n)];
    case 'clumsy':
      return [fmt(row.run.mistakes ?? 0), w.clumsy(row.run.title[lang].toUpperCase())];
    case 'slow':
      return [s.seconds(row.run.ms ?? 0), w.slow(row.run.title[lang].toUpperCase(), (row.run.ms ?? 0) > row.run.parMs)];
    case 'near':
      return [fmt(row.n), w.near(row.n)];
    case 'clean':
      return [fmt(row.n), w.clean(row.n)];
  }
}
